/**
 * CivitAI LoRA stack helpers — adapters, not engines.
 */

import type { InferenceModel } from "./types.js";

export type CatalogResourceKind = "checkpoint" | "lora" | "motion" | "other";

export type ResourceKindFilter = "all" | CatalogResourceKind;

export type SelectedLora = {
  modelId: string;
  versionId: string;
  name: string;
  civitaiType: string;
  baseModel?: string;
  strength: number;
  triggerWords?: string[];
  previewUrl?: string;
};

export const MAX_LORAS = 3;
export const DEFAULT_LORA_STRENGTH = 0.8;

export function catalogResourceKind(civitaiType: string | undefined | null): CatalogResourceKind {
  const t = (civitaiType || "checkpoint").toLowerCase();
  if (t === "lora" || t === "locon" || t === "lycoris" || t === "dora" || t === "loha" || t === "lokr") {
    return "lora";
  }
  if (t.includes("motion")) return "motion";
  if (t === "checkpoint" || t === "") return "checkpoint";
  return "other";
}

export function modelResourceKind(m: Pick<InferenceModel, "civitaiType" | "resourceKind">): CatalogResourceKind {
  if (m.resourceKind) return m.resourceKind;
  return catalogResourceKind(m.civitaiType);
}

export function isLoraKind(kind: CatalogResourceKind): boolean {
  return kind === "lora";
}

export function loraKey(lora: { modelId: string; versionId: string }): string {
  return `${lora.modelId}@${lora.versionId}`;
}

export function clampLoraStrength(n: unknown): number {
  const x = Number(n);
  if (!Number.isFinite(x)) return DEFAULT_LORA_STRENGTH;
  return Math.max(0, Math.min(2, Math.round(x * 100) / 100));
}

export function modelToSelectedLora(m: InferenceModel, strength = DEFAULT_LORA_STRENGTH): SelectedLora | null {
  const modelId = String(m.modelId || "").trim();
  if (!modelId) return null;
  const versionId = String(m.versionId || "").trim() || (modelId.includes("/") ? "main" : "");
  if (!versionId) return null;
  return {
    modelId,
    versionId,
    name: m.name || modelId,
    civitaiType: m.civitaiType || "LORA",
    baseModel: m.baseModel,
    strength: clampLoraStrength(strength),
    triggerWords: m.trainedWords?.length ? [...m.trainedWords] : undefined,
    previewUrl: m.previewUrl,
  };
}

export type AddLoraResult =
  | { ok: true; stack: SelectedLora[]; added: boolean }
  | { ok: false; stack: SelectedLora[]; error: string };

export function addLoraToStack(stack: SelectedLora[], next: SelectedLora): AddLoraResult {
  const key = loraKey(next);
  if (stack.some((s) => loraKey(s) === key)) {
    return { ok: true, stack, added: false };
  }
  if (stack.length >= MAX_LORAS) {
    return { ok: false, stack, error: `Remove a LoRA first (max ${MAX_LORAS}).` };
  }
  return { ok: true, stack: [...stack, { ...next, strength: clampLoraStrength(next.strength) }], added: true };
}

export function removeLoraFromStack(stack: SelectedLora[], key: string): SelectedLora[] {
  return stack.filter((s) => loraKey(s) !== key);
}

export function setLoraStrength(stack: SelectedLora[], key: string, strength: number): SelectedLora[] {
  return stack.map((s) => (loraKey(s) === key ? { ...s, strength: clampLoraStrength(strength) } : s));
}

export function filterByResourceKind<T extends { civitaiType?: string; resourceKind?: CatalogResourceKind }>(
  rows: T[],
  filter: ResourceKindFilter,
): T[] {
  if (filter === "all") return rows;
  return rows.filter((m) => {
    const kind = modelResourceKind(m);
    if (filter === "checkpoint") return kind === "checkpoint" || kind === "motion";
    return kind === filter;
  });
}

export function loraMatchesEngine(
  loraBase: string | undefined,
  engineBase: string | undefined,
): boolean {
  if (!loraBase || !engineBase) return true;
  return normalizeBase(loraBase) === normalizeBase(engineBase);
}

function normalizeBase(base: string): string {
  const b = base.toLowerCase();
  if (b.includes("flux.2") || b.includes("flux2")) return "flux2";
  if (b.includes("flux")) return "flux1";
  if (b.includes("qwen")) return "qwen";
  if (b.includes("z-image") || b.includes("zimage")) return "zimage";
  if (b.includes("ernie")) return "ernie";
  if (b.includes("sdxl") || b.includes("pony") || b.includes("illustrious") || b.includes("noobai")) {
    return "sdxl";
  }
  if (b.includes("sd 3") || b.includes("sd3")) return "sd3";
  if (b.includes("wan") || b.includes("svd") || b.includes("hunyuan") || b.includes("ltx")) return "wan";
  return "sd1";
}

export function resourceKindLabel(kind: CatalogResourceKind): string {
  if (kind === "lora") return "LoRA";
  if (kind === "motion") return "Motion";
  if (kind === "other") return "Other";
  return "Checkpoint";
}

/** CivitAI `/models` `types` query for a Type chip. All = omit (mixed page). */
export function civitaiTypesQuery(filter: ResourceKindFilter): string | undefined {
  if (filter === "all") return undefined;
  if (filter === "checkpoint") return "Checkpoint,MotionModule";
  if (filter === "lora") return "LORA,LoCon,DoRA";
  if (filter === "motion") return "MotionModule";
  return [
    "TextualInversion",
    "Hypernetwork",
    "AestheticGradient",
    "Controlnet",
    "Upscaler",
    "VAE",
    "TextEncoder",
    "UNet",
    "CLIPVision",
    "Poses",
    "Wildcards",
    "Workflows",
    "ComfyWorkflows",
    "Detection",
    "VisionLanguage",
    "CLIP",
    "LLM",
    "Other",
  ].join(",");
}
